import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import AxiosService from "../../services/AxiosHelper";

const baseURL = process.env.REACT_APP_BASEURL;

// --- 0. Sign Up Thunk ---
export const signUp = createAsyncThunk(
    "auth/signUp",
    async (values, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/auth/signup`,
                data: values,
            });
            toast.success("Account created! Please check your email to verify.");
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Signup failed");
        }
    }
);

// --- Verify Email Thunk ---
export const verifyEmail = createAsyncThunk(
    "auth/verifyEmail",
    async (token, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "get",
                url: `${baseURL}/auth/verify-email?token=${token}`,
            });
            toast.success("Email verified successfully!");
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Verification failed");
        }
    }
);

// --- Update Onboarding Step Thunk ---
export const updateOnboardingStep = createAsyncThunk(
    "auth/updateOnboardingStep",
    async (step, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "patch",
                url: `${baseURL}/auth/onboarding/step`,
                data: { step },
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to update step");
        }
    }
);

// --- Forgot Password Thunk ---
export const forgotPassword = createAsyncThunk(
    "auth/forgotPassword",
    async (email, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/auth/forgot-password`,
                data: { email },
            });
            toast.success("If an account exists, a reset link has been sent.");
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Request failed");
        }
    }
);

// --- Reset Password Thunk ---
export const resetPassword = createAsyncThunk(
    "auth/resetPassword",
    async ({ token, newPassword }, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/auth/reset-password`,
                data: { token, newPassword },
            });
            toast.success("Password reset successful!");
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Reset failed");
        }
    }
);

// --- Create Subscription Checkout Thunk ---
export const createSubscriptionCheckout = createAsyncThunk(
    "auth/createSubscriptionCheckout",
    async (tier, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/auth/create-subscription-checkout`,
                data: { tier },
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to create checkout session");
        }
    }
);

// --- 1. Login Thunk ---
export const logIn = createAsyncThunk(
    "auth/logIn",
    async (values, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/auth/login`,
                data: values,
            });
            localStorage.setItem("token", response.data.data.token);
            localStorage.setItem("masterCustomerId", response.data.data.user.id);
            localStorage.setItem("activeBusinessId", response.data.data.user.primaryBusinessId);
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Login failed");
        }
    }
);

// --- 2. Get Profile ---
export const getProfile = createAsyncThunk(
    "auth/getProfile",
    async (_, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "get",
                url: `${baseURL}/auth/profile`,
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to fetch profile");
        }
    }
);

// --- 3. Update Profile Settings ---
export const updateSettings = createAsyncThunk(
    "auth/updateSettings",
    async (values, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "patch",
                url: `${baseURL}/auth/settings`,
                data: values,
            });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to update profile");
        }
    }
);

// --- 4. Delete Master Account ---
export const deleteMasterAccount = createAsyncThunk(
    "auth/deleteMasterAccount",
    async (_, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "delete",
                url: `${baseURL}/auth/delete-account`,
            });
            localStorage.clear();
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Deletion failed");
        }
    }
);

// --- 5. Update Password ---
export const updatePassword = createAsyncThunk(
    "auth/updatePassword",
    async (values, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "patch",
                url: `${baseURL}/auth/change-password`,
                data: values,
            });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Password change failed");
        }
    }
);

// --- 13. Developer Platform Thunks ---
export const getDeveloperKeys = createAsyncThunk(
    "auth/getDeveloperKeys",
    async (_, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "get",
                url: `${baseURL}/developer/v1/keys`,
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to fetch developer keys");
        }
    }
);

export const rotateDeveloperKey = createAsyncThunk(
    "auth/rotateDeveloperKey",
    async (_, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/developer/v1/keys/rotate`,
            });
            toast.success("API Key rotated successfully");
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Key rotation failed");
        }
    }
);

export const getWebhooks = createAsyncThunk(
    "auth/getWebhooks",
    async (_, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "get",
                url: `${baseURL}/developer/v1/webhooks`,
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to fetch webhooks");
        }
    }
);

export const addWebhook = createAsyncThunk(
    "auth/addWebhook",
    async (webhookData, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/developer/v1/webhooks`,
                data: webhookData,
            });
            toast.success("Webhook registered successfully");
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to register webhook");
        }
    }
);

export const deleteWebhook = createAsyncThunk(
    "auth/deleteWebhook",
    async (id, { rejectWithValue }) => {
        try {
            await AxiosService.AxiostRequest({
                method: "delete",
                url: `${baseURL}/developer/v1/webhooks/${id}`,
            });
            toast.success("Webhook removed");
            return id;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to remove webhook");
        }
    }
);

export const addAllowedOrigin = createAsyncThunk(
    "auth/addAllowedOrigin",
    async (origin, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/developer/v1/origins`,
                data: { origin },
            });
            toast.success("Allowed origin added");
            return response.data.data; // Updated origins array
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to add origin");
        }
    }
);

export const getUsageStats = createAsyncThunk(
    "auth/getUsageStats",
    async (_, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "get",
                url: `${baseURL}/developer/v1/usage`,
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to fetch usage stats");
        }
    }
);

const authSlice = createSlice({
    name: "auth",
    initialState: {
        token: localStorage.getItem("token") || null,
        masterCustomerId: localStorage.getItem("masterCustomerId") || null,
        loading: false,
        passwordLoading: false,
        userDetail: null,
        developerData: {
            keys: null,
            webhooks: [],
            usage: null
        }
    },
    reducers: {
        logout: (state) => {
            state.token = null;
            state.userDetail = null;
            state.masterCustomerId = null;
            localStorage.removeItem("token");
            localStorage.removeItem("masterCustomerId");
            localStorage.removeItem("activeBusinessId");
            toast.info("Logged out safely");
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(signUp.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.token = payload.token;
                state.userDetail = payload.user;
            })
            .addCase(logIn.pending, (state) => {
                state.loading = true;
            })
            .addCase(logIn.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.token = payload.token;
                state.masterCustomerId = payload.user.id;
                state.userDetail = payload.user;
                toast.success("Login successful");
            })
            .addCase(logIn.rejected, (state, { payload }) => {
                state.loading = false;
                toast.error(payload?.message || "Login failed");
            })
            .addCase(getProfile.pending, (state) => {
                state.loading = true;
            })
            .addCase(getProfile.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.userDetail = payload;
            })
            .addCase(getProfile.rejected, (state) => {
                state.loading = false;
            })
            .addCase(updateOnboardingStep.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.userDetail = payload;
            })
            .addCase(updateSettings.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateSettings.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.userDetail = { ...state.userDetail, ...payload.data };
                toast.success("Profile updated successfully");
            })
            .addCase(updateSettings.rejected, (state, { payload }) => {
                state.loading = false;
                toast.error(payload?.message || "Failed to update profile");
            })
            // Developer Platform Cases
            .addCase(getDeveloperKeys.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.developerData.keys = payload;
            })
            .addCase(rotateDeveloperKey.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.developerData.keys = payload;
                if (state.userDetail) state.userDetail.apiKey = payload.apiKey;
            })
            .addCase(getWebhooks.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.developerData.webhooks = payload;
            })
            .addCase(addWebhook.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.developerData.webhooks.push(payload);
            })
            .addCase(deleteWebhook.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.developerData.webhooks = state.developerData.webhooks.filter(w => w._id !== payload);
            })
            .addCase(addAllowedOrigin.fulfilled, (state, { payload }) => {
                state.loading = false;
                if (state.userDetail) state.userDetail.allowedOrigins = payload;
            })
            .addCase(getUsageStats.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.developerData.usage = payload;
            });

        // Handle Password Actions Loading
        builder.addMatcher(
            (action) => [forgotPassword.pending.type, resetPassword.pending.type, updatePassword.pending.type].includes(action.type),
            (state) => { state.passwordLoading = true; }
        );
        builder.addMatcher(
            (action) => [
                forgotPassword.fulfilled.type, resetPassword.fulfilled.type, updatePassword.fulfilled.type,
                forgotPassword.rejected.type, resetPassword.rejected.type, updatePassword.rejected.type
            ].includes(action.type),
            (state, { payload, type }) => {
                state.passwordLoading = false;
                if (type === updatePassword.fulfilled.type) toast.success("Password updated successfully");
                if ([forgotPassword.rejected.type, resetPassword.rejected.type, updatePassword.rejected.type].includes(type)) {
                    toast.error(payload?.message || "Something went wrong");
                }
            }
        );

        // General loading matchers for all auth actions
        builder.addMatcher(
            (action) => action.type.startsWith("auth/") && action.type.endsWith("/pending") && !action.type.toLowerCase().includes("password"),
            (state) => { state.loading = true; }
        );
        builder.addMatcher(
            (action) => action.type.startsWith("auth/") && action.type.endsWith("/rejected") && !action.type.toLowerCase().includes("password"),
            (state, { payload }) => {
                state.loading = false;
                toast.error(payload?.message || "Something went wrong");
            }
        );
    },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
